class CartEditClipHandler {
    constructor() {
        this.init();
    }
    init() {
        document.body.addEventListener("click", this.handleButtonClick.bind(this));
    }
    async handleButtonClick(event) {
        const button = event.target.closest("[data-cart-item-edit]");
        if (!button) return;
        event.preventDefault();
        try {
            const itemKey = button.dataset.cartItemEdit;
            const configId = button.dataset.configId;
            const clipSequence = button.dataset.clipSequence;
            const response = await fetch("/cart.js");
            if (!response.ok) {
                throw new Error("Failed to fetch cart");
            }
            const cartData = await response.json();
            const clickedItem = cartData.items.find(item => item.key === itemKey);
            if (!clickedItem) {
                throw new Error("Item not found in cart");
            }
            const isChildClip = !!clickedItem.properties["_Parent Config ID"];
            const isFrameParent = !isChildClip && (clickedItem.properties["_Item Type"] || "").includes("FRAME");
            console.log("[CartEditClipHandler] Item analysis:", {
                itemKey: itemKey,
                configId: configId,
                isChildClip: isChildClip,
                isFrameParent: isFrameParent,
                itemType: clickedItem.properties["_Item Type"] || "(none)",
                totalCartItems: cartData.items.length
            });
            let frameParentItem = null;
            if (isChildClip) {
                const parentConfigId = clickedItem.properties["_Parent Config ID"];
                frameParentItem = cartData.items.find(item => item.properties["_Configuration ID"] === parentConfigId && !item.properties["_Parent Config ID"]);
            }
            let clipChildItem = null;
            if (isFrameParent) {
                clipChildItem = cartData.items.find(item => {
                    if (!item.properties) return false;
                    const parentId = item.properties["_Parent Config ID"];
                    const itemType = item.properties["_Item Type"] || "";
                    return parentId === configId && itemType.includes("Clip");
                });
                if (!clipChildItem) {
                    clipChildItem = cartData.items.find(item => {
                        if (!item.properties) return false;
                        return item.properties["_Parent Config ID"] === configId && !!item.properties["_Clip Type"];
                    });
                    if (clipChildItem) {
                        console.log("[CartEditClipHandler] Found clip child via _Clip Type fallback search");
                    }
                }
                if (!clipChildItem) {
                    const childrenOfConfig = cartData.items.filter(item => item.properties && item.properties["_Parent Config ID"] === configId);
                    console.warn("[CartEditClipHandler] ⚠️ Clip child NOT found for frame parent:", {
                        configId: configId,
                        childrenFound: childrenOfConfig.length,
                        childTypes: childrenOfConfig.map(c => c.properties["_Item Type"] || "(none)"),
                        childClipTypes: childrenOfConfig.map(c => c.properties["_Clip Type"] || "(none)"),
                        frameHasClipType: !!clickedItem.properties["_Clip Type"],
                        allCartItemTypes: cartData.items.map(i => ({
                            type: i.properties?.["_Item Type"] || "(none)",
                            configId: i.properties?.["_Configuration ID"] || "",
                            parentId: i.properties?.["_Parent Config ID"] || ""
                        }))
                    });
                    if (clickedItem.properties["_Clip Type"] || clickedItem.properties["_Clip Type Key"]) {
                        console.log("[CartEditClipHandler] Using frame parent clip metadata as fallback");
                    }
                } else {
                    console.log("[CartEditClipHandler] ✅ Found clip child:", {
                        clipType: clipChildItem.properties["_Clip Type"],
                        clipSequence: clipChildItem.properties["_Clip Sequence"],
                        itemType: clipChildItem.properties["_Item Type"]
                    });
                }
            }
            const orderType = button.dataset.orderType || clickedItem.properties["_Order Type"] || "";
            const isPrescriptionOnly = orderType === "Prescription Only";
            let editConfigData;
            if (isPrescriptionOnly) {
                editConfigData = this.extractConfigFromPrescriptionOnlyItem(clickedItem);
            } else if (isFrameParent && clipChildItem) {
                editConfigData = this.extractConfigFromCartItem(clipChildItem, clickedItem);
            } else if (isFrameParent && !clipChildItem) {
                editConfigData = this.extractConfigFromCartItem(clickedItem, null);
                console.warn("[CartEditClipHandler] Extracting from frame parent only (clip child missing)");
            } else {
                editConfigData = this.extractConfigFromCartItem(clickedItem, frameParentItem);
            }
            let effectiveClipSequence = clipSequence || "";
            if (isFrameParent && clipChildItem) {
                effectiveClipSequence = clipChildItem.properties["_Clip Sequence"] || "1";
            } else if (isFrameParent && !clipChildItem) {
                effectiveClipSequence = "1";
            }
            editConfigData.accessoryVariantIds = cartData.items.filter(line => line.properties && line.properties["_Accessory Name"] && line.properties["_Parent Config ID"] === configId && String(line.properties["_Clip Sequence"] || "") === String(effectiveClipSequence || "")).map(line => line.variant_id);
            localStorage.setItem("isEditingConfig", "true");
            localStorage.setItem("editConfigId", configId);
            localStorage.setItem("editClipSequence", effectiveClipSequence);
            localStorage.setItem("editConfigData", JSON.stringify(editConfigData));
            localStorage.setItem("jumpToStep", "5");
            if (isPrescriptionOnly) {
                localStorage.setItem("editPrescriptionOnly", "true");
            }
            button.textContent = "Loading...";
            button.disabled = true;
            window.location.href = "/pages/order-flow";
        } catch (error) {
            console.error("[CartEditClipHandler] Error:", error);
            alert("Error loading configuration. Please try again.");
            if (button) {
                button.textContent = "Edit";
                button.disabled = false;
            }
        }
    }
    _parsePdProperty(pdRaw) {
        const raw = (pdRaw || "").trim();
        if (!raw) return null;
        if (raw.startsWith("L:") && raw.includes("R:")) {
            const parts = raw.replace(/[LR]:\s*/g, "").split(",").map(s => s.trim());
            return {
                mode: "dual",
                value: null,
                left: parts[0] || null,
                right: parts[1] || null
            };
        }
        return {
            mode: "single",
            value: raw,
            left: null,
            right: null
        };
    }
    extractConfigFromCartItem(item, frameParentItem = null) {
        const props = item.properties;
        const frameProps = frameParentItem ? frameParentItem.properties : {};
        const clipTypeMap = {
            "Sun Clip": "sun",
            "Reader Clip": "reader",
            "Blue Light Clip": "blueLight",
            "3D Clip": "3d",
            "Light Sensitivity Clip": "lightSensitivity",
            "Migraine Clip": "migraine",
            "Night Drive Clip": "driving",
            Sun: "sun",
            Reader: "reader",
            "Blue Light": "blueLight",
            "3D": "3d",
            "Light Sensitivity": "lightSensitivity",
            Migraine: "migraine",
            "Night Drive": "driving",
            "Night Driving": "driving",
            Therapeutic: "therapeutic"
        };
        const clipTypeLabel = props["_Clip Type"] || frameProps["_Clip Type"] || "";
        let clipType = clipTypeMap[clipTypeLabel];
        if (!clipType && clipTypeLabel) {
            const normalizedLabel = clipTypeLabel.replace(/\s*clip$/i, "").trim();
            clipType = clipTypeMap[normalizedLabel];
        }
        if (!clipType && clipTypeLabel) {
            const stripped = clipTypeLabel.replace(/\s*clip$/i, "").trim();
            clipType = stripped.toLowerCase().replace(/\s+(.)/g, (_, c) => c.toUpperCase()).replace(/\s+/g, "");
        }
        if (!clipType) {
            clipType = props["_Clip Type Key"] || frameProps["_Clip Type Key"] || null;
        }
        let prescription = null;
        const prescriptionProp = props["_Prescription"] || frameProps["_Prescription"];
        if (prescriptionProp && prescriptionProp !== "skip") {
            try {
                prescription = JSON.parse(prescriptionProp);
            } catch (e) {
                console.warn("[extractConfigFromCartItem] Error parsing prescription:", e);
                prescription = prescriptionProp;
            }
        } else if (prescriptionProp === "skip") {
            prescription = "skip";
        }
        const uploadPd = !prescription ? this._parsePdProperty(props["PD"] || frameProps["PD"]) : null;
        const config = {
            frameChoice: props["_Frame Choice Key"] || props["_Frame Choice"] || frameProps["_Frame Choice Key"] || frameProps["_Frame Choice"] || "own-frame-own-lenses",
            frameBrand: props["_Frame Brand"] || frameProps["_Frame Brand"] || "",
            frameModel: props["_Frame Model"] || frameProps["_Frame Model"] || "",
            frameProduct: props["_Frame Product ID"] || frameProps["_Frame Product ID"] || frameProps["_Frame Handle"] || "",
            frameVariantId: props["_Frame Variant ID"] || frameProps["_Frame Variant ID"] || null,
            frameVariantTitle: props["_Frame Color"] || frameProps["_Frame Color"] || null,
            orderSource: props["_Order Source"] || frameProps["_Order Source"] || null,
            nativeLensType: props["_Prescription Lens Type"] || props["_Native Lens Type"] || frameProps["_Native Lens Type"] || "single-vision",
            nativeLensFeature: props["_Prescription Lens Feature"] || props["_Native Lens Feature"] || frameProps["_Native Lens Feature"] || "standard",
            nativeLensMaterial: props["_Prescription Lens Material"] || props["_Native Lens Material"] || frameProps["_Native Lens Material"] || "standard",
            prescription: prescription,
            uploadPd: uploadPd,
            rxUploadToken: props["_Rx Upload Token"] || frameProps["_Rx Upload Token"] || null,
            rxUploadFilename: props["_Rx Upload Filename"] || frameProps["_Rx Upload Filename"] || null,
            prescriptionStatus: props["_Prescription Status"] || frameProps["_Prescription Status"] || null,
            clipType: clipType,
            lensCategory: (props["_Lens Category"] || frameProps["_Lens Category"] || "").toLowerCase().replace(/\s+/g, "-") || null,
            lensColor: props["_Lens Color"] || frameProps["_Lens Color"] || null,
            magnification: props["_Magnification"] || frameProps["_Magnification"] || null,
            therapeuticType: (props["_Therapeutic Type"] || frameProps["_Therapeutic Type"] || (props["_FL-41 Intensity"] || frameProps["_FL-41 Intensity"] ? "fl41" : "")).toLowerCase() || null,
            therapeuticIntensity: (props["_FL-41 Intensity"] || frameProps["_FL-41 Intensity"] || props["_Therapeutic Intensity"] || frameProps["_Therapeutic Intensity"] || "").toLowerCase() || null,
            bridgeFinish: props["_Bridge Finish"] || frameProps["_Bridge Finish"] || "MatteBlack",
            magnetFinish: props["_Magnet Finish"] || frameProps["_Magnet Finish"] || "Gunmetal",
            crystalAccent: props["_Crystal Accent"] || frameProps["_Crystal Accent"] || null,
            originalItemKey: item.key,
            variantId: item.variant_id
        };
        console.log("[extractConfigFromCartItem] Extracted config:", {
            clipType: config.clipType,
            lensColor: config.lensColor,
            frameProduct: config.frameProduct,
            frameVariantId: config.frameVariantId,
            bridgeFinish: config.bridgeFinish,
            magnetFinish: config.magnetFinish
        });
        return config;
    }
    extractConfigFromPrescriptionOnlyItem(item) {
        const props = item.properties;
        let prescription = null;
        const prescriptionProp = props["_Prescription"];
        if (prescriptionProp && prescriptionProp !== "skip") {
            try {
                prescription = JSON.parse(prescriptionProp);
            } catch (e) {
                console.warn("[extractConfigFromPrescriptionOnlyItem] Error parsing prescription:", e);
                prescription = prescriptionProp;
            }
        } else if (prescriptionProp === "skip") {
            prescription = "skip";
        }
        if (!prescription && props["OD Sphere"]) {
            const pdObj = this._parsePdProperty(props["PD"]);
            prescription = {
                od: {
                    sphere: props["OD Sphere"] || "",
                    cylinder: props["OD Cylinder"] || "",
                    axis: props["OD Axis"] || "",
                    add: props["OD Add"] || ""
                },
                os: {
                    sphere: props["OS Sphere"] || "",
                    cylinder: props["OS Cylinder"] || "",
                    axis: props["OS Axis"] || "",
                    add: props["OS Add"] || ""
                },
                pd: pdObj,
                notes: props["Rx Notes"] || ""
            };
            console.log("[extractConfigFromPrescriptionOnlyItem] Rebuilt prescription from individual fields");
        }
        const uploadPd = !prescription ? this._parsePdProperty(props["PD"]) : null;
        return {
            frameChoice: props["_Frame Choice Key"] || "new-frame-lenses",
            frameBrand: "",
            frameModel: "",
            frameProduct: props["_Frame Product ID"] || props["_Frame Handle"] || "",
            frameVariantId: props["_Frame Variant ID"] || null,
            frameVariantTitle: props["_Frame Color"] || null,
            orderSource: props["_Order Source"] || null,
            nativeLensType: props["_Lens Type"] || props["_Native Lens Type"] || "single-vision",
            nativeLensFeature: props["_Lens Feature"] || props["_Native Lens Feature"] || "standard",
            nativeLensMaterial: props["_Lens Material"] || props["_Native Lens Material"] || "standard",
            prescription: prescription,
            uploadPd: uploadPd,
            rxUploadToken: props["_Rx Upload Token"] || null,
            rxUploadFilename: props["_Rx Upload Filename"] || null,
            prescriptionStatus: props["_Prescription Status"] || null,
            clipType: null,
            lensCategory: null,
            lensColor: null,
            magnification: null,
            therapeuticType: null,
            therapeuticIntensity: null,
            bridgeFinish: null,
            magnetFinish: null,
            crystalAccent: null,
            isPrescriptionOnly: true,
            originalItemKey: item.key,
            variantId: item.variant_id
        };
    }
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
        window.cartEditClipHandler = new CartEditClipHandler;
    });
} else {
    window.cartEditClipHandler = new CartEditClipHandler;
}

window.CartEditClipHandler = CartEditClipHandler;